import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Box, getSize, Grid, HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {css} from 'styled-components';
import {array, object, string} from 'yup';

import {useEffect, useMemo, useRef} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  CommonConditionAttribute,
  CommonDescriptionAttribute,
  CommonDigitalCertificateAttribute,
  CommonDigitalCertificateThemeAttribute,
  CommonGenerationAttribute,
  CommonHighlightsAttribute,
  CommonModelRangeAttribute,
  CommonSubtitleAttribute,
  CommonTitleExtensionAttribute,
  CommonTrimLineAttribute,
  CommonVersionAttribute,
  CommonVideoUrlAttribute,
  GetVehicleAdAttributesApiRes,
  useGetMobileDeModelRangeOptionsQuery,
  useGetMobileDeTrimLineOptionsQuery,
  useGetSalesPlatformsQuery,
  useGetVehicleAdAttributesQuery,
  useGetVehicleQuery,
  VehicleGeneralAdvertisingSettingsResponseBody,
  GetSalesPlatformsApiRes,
  GeneratedPlatformDescription,
  useConfirmGeneratedDescriptionsMutation,
} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {
  AiVehicleDescriptionBanner,
  handleApiError,
  PredefinedNotes,
  usePermissions,
} from '@dms/shared';
import {TITLE_EXTENSION_MAX_LENGTH} from '@dms/teas';

import {ApiError, DOT_CHARACTER, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {HIGHLIGHT_MAX_LENGTH} from '../constants/highlightMaxLength';
import {useGeneratedDescriptions} from '../contexts/GeneratedDescriptionsContext';
import {DescriptionGeneratorInputs} from '../types/DescriptionGeneratorInputs';
import {
  GeneralSettingsFormValues,
  LocalizedContentValues,
} from '../types/GeneralSettingsFormValues';
import {getAttributeByCode} from '../utils/getAttributeByCode';
import {AdContentCard} from './AdContentCard';
import {AdServerAttributeSection} from './AdServerAttributeSection';
import {AdvertisementPublishErrors} from './AdvertisementPublishErrors';
import {ChangeDigitalCertificateThemeDynamically} from './ChangeDigitalCertificateThemeDynamically';
import {DescriptionFormField} from './DescriptionFormField';
import {DescriptionGenerator} from './DescriptionGenerator';
import {MultiLanguageContentSection} from './MultiLanguageContentSection';
import {RegisterFormSubmit} from './RegisterFormSubmit';
import {SubtitleFormField} from './SubtitleFormField';
import {TitleFormField} from './TitleFormField';

const CONDITION_OPTIONS: {label: string; value: string}[] = [
  {label: i18n.t('page.advertisement.labels.condition.excellent'), value: '1'},
  {label: i18n.t('page.advertisement.labels.condition.veryGood'), value: '2'},
  {label: i18n.t('page.advertisement.labels.condition.good'), value: '3'},
  {label: i18n.t('page.advertisement.labels.condition.acceptable'), value: '4'},
  {label: i18n.t('page.advertisement.labels.condition.serviceable'), value: '5'},
];

export interface GeneralSettingsFormProps extends TestIdProps {
  vehicleId: string;
  descriptionLabel: string;
  data: VehicleGeneralAdvertisingSettingsResponseBody | undefined;
  versionOptions: Array<{value: string; label: string}>;
  generationOptions: Array<{value: string; label: string}>;
  digitalCertificateOptions: Array<{value: string; label: string}>;
  digitalCertificateThemeOptions: Array<{value: string; label: string}>;
  isDisabled: boolean;
  isMultilanguageActive?: boolean;
  enabledLanguages?: Array<{value: string; label: string}>;
  platforms?: GetSalesPlatformsApiRes;
  initialLocalizedContent?: Record<string, LocalizedContentValues>;
  onSubmit: FormSubmitHandler<GeneralSettingsFormValues>;
  onHandleEdit: () => void;
}

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function GeneralSettingsForm(props: GeneralSettingsFormProps) {
  const formatDateTime = useDateTimeFormatter();
  const {data: platforms} = useGetSalesPlatformsQuery({vehicleId: props.vehicleId});
  const [canGenerateDescription] = usePermissions({
    permissionKeys: ['vehicleSalesGenerateDescription'],
  });
  const isOtomotoEnabled = platforms?.find(
    ({platform}) => platform.code === AdvertisingPlatformCodeEnum.OTOMOTO
  )?.platform.enabled;
  const isPerPlatformAttributesEnabled = isFeatureEnabled(
    featureFlags.ADVERTISEMENT_PER_PLATFORM_ATTRIBUTES
  );

  // Lifted from AdServerAttributeSection so options.suggestion can flow into form defaultValues.
  // `<Form values=>` with `keepDirtyValues` reapplies non-dirty fields when the query resolves async,
  // so manual edits survive while suggestions land. Replaces imperative setValue + useEffect.
  // Single round-trip per attribute: catalog + suggestion in one EP.
  const {data: adAttributes} = useGetVehicleAdAttributesQuery(
    isPerPlatformAttributesEnabled ? {vehicleId: props.vehicleId} : skipToken
  );
  const enabledPlatformCodes = new Set(
    (platforms ?? [])
      .filter((platformData) => platformData.platform.enabled && platformData.enabled)
      .map(({platform}) => platform.code)
  );
  const isAttributeVisible = (
    linkedPlatforms: GetVehicleAdAttributesApiRes[keyof GetVehicleAdAttributesApiRes] | undefined
  ) => (linkedPlatforms ?? []).some((p) => enabledPlatformCodes.has(p));
  const isModelRangeLinked = isAttributeVisible(adAttributes?.modelRange);
  const isTrimLineLinked = isAttributeVisible(adAttributes?.trimLine);
  // Truck-homologated cars publish as VanUpTo7500, which has no model range or trim line catalogue.
  const {data: vehicle} = useGetVehicleQuery(
    {vehicleId: props.vehicleId},
    {skip: !isModelRangeLinked && !isTrimLineLinked}
  );
  const hasModelCatalogue = isNotNil(vehicle) && vehicle.truckHomologation !== true;
  const modelRangeVisible = hasModelCatalogue && isModelRangeLinked;
  const trimLineVisible = hasModelCatalogue && isTrimLineLinked;
  const modelRangeOptionsQuery = useGetMobileDeModelRangeOptionsQuery(
    modelRangeVisible ? {vehicleId: props.vehicleId} : skipToken
  );
  const trimLineOptionsQuery = useGetMobileDeTrimLineOptionsQuery(
    trimLineVisible ? {vehicleId: props.vehicleId} : skipToken
  );

  const digitalCertificateTheme = getAttributeByCode<CommonDigitalCertificateThemeAttribute>(
    'digital_certificate_theme',
    props.data?.attributes
  )?.value;

  const conditionFormValue = getAttributeByCode<CommonConditionAttribute>(
    'condition',
    props.data?.attributes
  )?.value?.toString();
  const subtitle =
    getAttributeByCode<CommonSubtitleAttribute>('subtitle', props.data?.attributes)?.value ?? null;
  // Required for correctness, not performance: <Form values={defaultValues}> wires
  // straight into react-hook-form's `useForm({values})`, which re-runs its sync effect
  // on every reference change. A fresh object literal here cascades into watcher
  // setStates and (combined with formApi.watch root re-renders) trips React's
  // "Maximum update depth exceeded" guard while typing in the description field.
  const defaultValues = useMemo<GeneralSettingsFormValues>(() => {
    const highlightsValue = getAttributeByCode<CommonHighlightsAttribute>(
      'highlights',
      props.data?.attributes
    )?.value;

    return {
      description:
        getAttributeByCode<CommonDescriptionAttribute>('description', props.data?.attributes)
          ?.value ?? null,
      title:
        getAttributeByCode<CommonTitleExtensionAttribute>('title_extension', props.data?.attributes)
          ?.value ?? null,
      subtitle,
      version:
        getAttributeByCode<CommonVersionAttribute>('version', props.data?.attributes)?.value ??
        null,
      generation:
        getAttributeByCode<CommonGenerationAttribute>('generation', props.data?.attributes)
          ?.value ?? null,
      digitalCertificate:
        getAttributeByCode<CommonDigitalCertificateAttribute>(
          'digital_certificate',
          props.data?.attributes
        )?.value ?? null,
      digitalCertificateTheme: digitalCertificateTheme ?? null,
      videoUrl:
        getAttributeByCode<CommonVideoUrlAttribute>('video_url', props.data?.attributes)?.value ??
        null,
      condition: isNotNil(conditionFormValue) ? [conditionFormValue] : null,
      highlight1: highlightsValue?.[0] ?? null,
      highlight2: highlightsValue?.[1] ?? null,
      highlight3: highlightsValue?.[2] ?? null,
      // Refdata suggestion is surfaced as an opt-in chip under the field
      // (see AdServerAttributeSection), not auto-applied — BE cannot distinguish
      // "user cleared" from "never set" once a save lands, so a fallback here
      // would re-populate cleared fields on every refetch.
      modelRange:
        getAttributeByCode<CommonModelRangeAttribute>('model_range', props.data?.attributes)
          ?.value ?? null,
      trimLine:
        getAttributeByCode<CommonTrimLineAttribute>('trim_line', props.data?.attributes)?.value ??
        null,
      localizedContent: props.initialLocalizedContent,
    };
  }, [
    props.data,
    digitalCertificateTheme,
    conditionFormValue,
    subtitle,
    props.initialLocalizedContent,
  ]);

  const descriptionGeneratorInputsRef = useRef<DescriptionGeneratorInputs | null>(null);
  const formApiRef = useRef<UseFormReturn<GeneralSettingsFormValues> | null>(null);

  // Generated platform descriptions live in shared context so the platform detail action
  // (a sibling subtree) can save them via the form submit (PATCH + confirm-generated-descriptions).
  const {
    generatedPlatformDescriptions,
    setGeneratedPlatformDescriptions,
    resetGeneratedPlatformDescriptions,
    formSubmitRef,
    isDirtyRef,
  } = useGeneratedDescriptions();

  const [confirmGeneratedDescriptions] = useConfirmGeneratedDescriptionsMutation();

  // Clear the submit bridge when the form unmounts (e.g. switching the inner tab) so the
  // platform detail action never calls submitForm on an unmounted form.
  useEffect(
    function clearFormSubmitBridge() {
      return () => {
        formSubmitRef.current = null;
        isDirtyRef.current = false;
      };
    },
    [formSubmitRef, isDirtyRef]
  );

  // Platform variants now arrive from generate-platform-descriptions (split out of
  // generate-description) for both single- and multi-language flows. The base content
  // is applied separately via onGenerate / onGenerateLocalized.
  const handlePlatformsGenerated = (generatedPlatforms: GeneratedPlatformDescription[]) => {
    setGeneratedPlatformDescriptions(generatedPlatforms);

    const failed = generatedPlatforms.filter((p) => isNotNil(p.error));

    if (failed.length === 0) {
      return;
    }

    const titleByCode = new Map<string, string>(
      platforms?.map((p) => [p.platform.code, p.platform.title]) ?? []
    );

    const platformNames = failed
      .map((p) => titleByCode.get(p.platformCode) ?? p.platformCode)
      .join(', ');

    showNotification.error(
      i18n.t('page.advertisement.notifications.platformGenerationPartiallyFailed', {
        platformNames,
      })
    );
  };

  const handleSubmit: FormSubmitHandler<GeneralSettingsFormValues> = async (
    values,
    setErrors,
    reset,
    blockerContext
  ) => {
    await props.onSubmit(
      values,
      setErrors,
      (...args) => {
        reset(...args);
        isDirtyRef.current = false;
      },
      blockerContext
    );

    if (isNil(generatedPlatformDescriptions)) {
      return;
    }

    const successful = generatedPlatformDescriptions.filter((p) => isNil(p.error));

    if (successful.length === 0) {
      resetGeneratedPlatformDescriptions();
      return;
    }

    await confirmGeneratedDescriptions({
      vehicleId: props.vehicleId,
      body: {
        description: values.description ?? '',
        platforms: successful.map((p) => ({
          platformCode: p.platformCode,
          description: p.content,
        })),
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(
          i18n.t('page.advertisement.notifications.platformDescriptionsConfirmed')
        );
        resetGeneratedPlatformDescriptions();
      })
      .catch((e: ApiError) => {
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure});
        resetGeneratedPlatformDescriptions();
      });
  };

  return (
    <Box position="relative">
      <VStack spacing={4}>
        <Show whenFeatureEnabled={featureFlags.ADVERTISEMENT_PREFLIGHT}>
          <AdvertisementPublishErrors vehicleId={props.vehicleId} />
        </Show>
        {/* Rendered outside the settings form: DescriptionGenerator is a form of its own */}
        <Show when={canGenerateDescription}>
          <AiVehicleDescriptionBanner />
          <DescriptionGenerator
            vehicleId={props.vehicleId}
            enabledLanguages={props.isMultilanguageActive ? props.enabledLanguages : undefined}
            inputsRef={descriptionGeneratorInputsRef}
            onGenerate={(content) =>
              formApiRef.current?.setValue('description', content, {shouldDirty: true})
            }
            onPlatformsGenerated={handlePlatformsGenerated}
            onGenerateLocalized={(results) => {
              results.forEach(({language, content}) => {
                formApiRef.current?.setValue(`localizedContent.${language}.description`, content, {
                  shouldDirty: true,
                });
              });
            }}
          />
        </Show>
        <Form<GeneralSettingsFormValues>
          values={defaultValues}
          resetOptions={{keepDirtyValues: true}}
          schema={schema}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi) => {
            formApiRef.current = formApi;

            return (
              <VStack spacing={4}>
                <RegisterFormSubmit
                  control={control}
                  formSubmitRef={formSubmitRef}
                  isDirtyRef={isDirtyRef}
                />
                <div
                  // Styling hack, but we need PredefinedNotes inside the form and positioned next to tab headers
                  css={css`
                    position: absolute;
                    top: 0;
                    right: 0;
                    transform: translateY(calc(-100% - ${getSize(4)}));
                  `}
                >
                  <PredefinedNotes
                    note={formApi.watch('description')}
                    onPrefill={(description) =>
                      formApi.setValue('description', description, {shouldDirty: true})
                    }
                    resource="VEHICLE"
                    context="advertisement"
                    data-testid={suffixTestId('predefinedNotes', props)}
                  />
                </div>
                {props.isMultilanguageActive && props.enabledLanguages ? (
                  // The general title_extension is mirrored from the default-language tab on
                  // submit (AdvertisementTab), so the base input has no separate role here.
                  <AdContentCard>
                    <MultiLanguageContentSection
                      control={control}
                      enabledLanguages={props.enabledLanguages}
                      vehicleId={props.vehicleId}
                      isDisabled={props.isDisabled}
                      platforms={props.platforms}
                      formApi={formApi}
                      descriptionGeneratorInputsRef={descriptionGeneratorInputsRef}
                    />
                  </AdContentCard>
                ) : (
                  <AdContentCard>
                    <VStack spacing={4}>
                      <TitleFormField
                        control={control}
                        name="title"
                        tooltip={i18n.t('page.advertisement.labels.titleExtensionTooltip')}
                        maxLength={TITLE_EXTENSION_MAX_LENGTH}
                        isDisabled={props.isDisabled}
                        platforms={platforms}
                        vehicleId={props.vehicleId}
                        getCurrentValue={() => formApi.getValues('title') ?? ''}
                        onGenerated={(text) => formApi.setValue('title', text, {shouldDirty: true})}
                        data-testid={suffixTestId('title', props)}
                      />
                      <SubtitleFormField
                        control={control}
                        name="subtitle"
                        isDisabled={props.isDisabled}
                        platforms={platforms}
                        data-testid={suffixTestId('subtitle', props)}
                      />
                      <DescriptionFormField
                        vehicleId={props.vehicleId}
                        control={control}
                        descriptionLabel={props.descriptionLabel}
                        isDisabled={props.isDisabled}
                        data-testid={props['data-testid']}
                      />
                    </VStack>
                  </AdContentCard>
                )}
                <Grid columns={[1, 2, 4]}>
                  <Show
                    when={isOtomotoEnabled && !isPerPlatformAttributesEnabled}
                    whenFeatureEnabled={featureFlags.ADVERTISEMENT_VERSION_GENERATION_ATTRIBUTES}
                  >
                    <FormField
                      control={control}
                      label={i18n.t('page.advertisement.labels.version')}
                      tooltip={i18n.t('page.advertisement.labels.versionTooltip')}
                      name="version"
                      type="choice"
                      options={props.versionOptions}
                      isDisabled={props.isDisabled}
                      data-testid={suffixTestId('version', props)}
                    />
                  </Show>
                  <Show
                    when={isOtomotoEnabled && !isPerPlatformAttributesEnabled}
                    whenFeatureEnabled={featureFlags.ADVERTISEMENT_VERSION_GENERATION_ATTRIBUTES}
                  >
                    <FormField
                      control={control}
                      label={i18n.t('page.advertisement.labels.generation')}
                      tooltip={i18n.t('page.advertisement.labels.generationTooltip')}
                      name="generation"
                      type="choice"
                      options={props.generationOptions}
                      isDisabled={props.isDisabled}
                      data-testid={suffixTestId('generation', props)}
                    />
                  </Show>
                  <>
                    <FormField
                      control={control}
                      label={i18n.t('page.advertisement.labels.digitalCertificate')}
                      tooltip={i18n.t('page.advertisement.labels.digitalCertificateTooltip')}
                      name="digitalCertificate"
                      type="choice"
                      options={props.digitalCertificateOptions}
                      isDisabled={props.isDisabled}
                      data-testid={suffixTestId('digitalCertificate', props)}
                    />
                    <ChangeDigitalCertificateThemeDynamically
                      formApi={formApi}
                      passedDigitalCertificateTheme={digitalCertificateTheme}
                    />
                    <FormField
                      control={control}
                      label={i18n.t('page.advertisement.labels.digitalCertificateTheme')}
                      tooltip={i18n.t('page.advertisement.labels.digitalCertificateThemeTooltip')}
                      name="digitalCertificateTheme"
                      type="choice"
                      options={props.digitalCertificateThemeOptions}
                      isDisabled={props.isDisabled}
                      data-testid={suffixTestId('digitalCertificateTheme', props)}
                    />
                    <FormField
                      control={control}
                      label={i18n.t('page.advertisement.labels.videoUrl')}
                      tooltip={i18n.t('page.advertisement.labels.videoUrlTooltip')}
                      name="videoUrl"
                      type="text"
                      isDisabled={props.isDisabled}
                      data-testid={suffixTestId('videoUrl', props)}
                    />
                  </>
                </Grid>
                <Show whenFeatureEnabled={featureFlags.ADVERTISEMENT_PER_PLATFORM_ATTRIBUTES}>
                  <AdServerAttributeSection
                    vehicleId={props.vehicleId}
                    control={control}
                    setValue={formApi.setValue}
                    attributes={adAttributes}
                    enabledPlatformCodes={enabledPlatformCodes}
                    isModelRangeVisible={modelRangeVisible}
                    isTrimLineVisible={trimLineVisible}
                    modelRangeOptionsQuery={modelRangeOptionsQuery}
                    trimLineOptionsQuery={trimLineOptionsQuery}
                    versionOptions={props.versionOptions}
                    generationOptions={props.generationOptions}
                    isDisabled={props.isDisabled}
                    data-testid={suffixTestId('adServerAttributes', props)}
                  />
                </Show>
                <HStack>
                  <FormField
                    control={control}
                    type="chips"
                    options={CONDITION_OPTIONS}
                    name="condition"
                    isDisabled={props.isDisabled}
                    label={i18n.t('page.advertisement.labels.conditionTitle')}
                    tooltip={i18n.t('page.advertisement.labels.conditionTooltip')}
                    data-testid={suffixTestId('condition', props)}
                    isDeselectable
                  />
                </HStack>
                <Show
                  when={
                    props.data?.updatedAt && isNotNil(props.data.updatedBy)
                      ? {updatedAt: props.data.updatedAt, updatedBy: props.data.updatedBy}
                      : null
                  }
                >
                  {(update) => (
                    <Text size="xSmall" color="secondary">
                      {`${i18n.t(`page.advertisement.labels.lastUpdateBy`)} ${
                        update.updatedBy
                      } ${DOT_CHARACTER} ${formatDateTime('dateTimeShort', parseDate(update.updatedAt))}`}
                    </Text>
                  )}
                </Show>
                <HStack justify="space-between">
                  <Link
                    leftIcon="image/edit"
                    title={i18n.t('page.advertisement.actions.editParameters')}
                    size="small"
                    onClick={props.onHandleEdit}
                    data-testid="button-advertisement.listingAttributes.edit"
                  />
                  <Show when={!props.isDisabled}>
                    <FormButton
                      type="submit"
                      control={control}
                      title={i18n.t('general.actions.confirm')}
                      data-testid={props['data-testid']}
                    />
                  </Show>
                </HStack>
              </VStack>
            );
          }}
        </Form>
      </VStack>
    </Box>
  );
}

const schema = object().shape({
  // No .max() on title: the input hard-stops at the highest enabled-platform limit
  // (TitleFormField) and per-platform overruns are soft warnings — a submit-blocking
  // validation here would undercut both. Length authority is the BE.
  title: string().nullable(),
  subtitle: string().nullable(),
  description: string().nullable(),
  videoUrl: string().url().nullable(),
  condition: array(string()).nullable(),
  highlight1: string().nullable().max(HIGHLIGHT_MAX_LENGTH),
  highlight2: string().nullable().max(HIGHLIGHT_MAX_LENGTH),
  highlight3: string().nullable().max(HIGHLIGHT_MAX_LENGTH),
  modelRange: string().nullable(),
  trimLine: string().nullable(),
});
